import { useEffect, useRef } from "react";
import { BlogCmsHeaderActions } from "@/components/blog-cms/BlogCmsHeaderActions";
import { BlogCmsStatePanel } from "@/components/blog-cms/BlogCmsStatePanel";
import { RetryCountdownText } from "@/components/blog-cms/RetryCountdownText";
import { HeaderActions } from "@/components/layout/HeaderActions";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { useBlogCmsFrame, type BlogCmsFrameState } from "@/hooks/useBlogCmsFrame";
import { useRetryCountdown } from "@/hooks/useRetryCountdown";
import { BLOG_CMS_COPY, rateLimitedToast } from "@/lib/blog-cms/copy";
import { openBlogCmsInNewTab } from "@/lib/blog-cms/open-in-new-tab";
import { BLOG_CMS_IFRAME_SANDBOX, type TicketFailure } from "@/lib/blog-cms/protocol";

type FrameState = Extract<BlogCmsFrameState, { kind: "frame" }>;

/**
 * `-mx-6 -mt-6 md:-mb-6` cancels `<main>`'s padding (`px-6 pb-20 pt-6 md:pb-6`) except, below `md`,
 * the 80 px that keeps the frame above the fixed bottom nav (RV2.5). `dvh` keeps iOS's collapsing
 * toolbar out of the height.
 */
const REGION_CLASS =
  "relative -mx-6 -mt-6 md:-mb-6 h-[calc(100dvh-var(--admin-header-height)-var(--admin-outer-gap)*2-5rem)] md:h-[calc(100dvh-var(--admin-header-height)-var(--admin-outer-gap)*2)]";

function RetryRenewalButton({ failure, onRetry }: { failure: TicketFailure; onRetry: () => void }) {
  const secondsLeft = useRetryCountdown(
    failure.reason === "rate-limited" ? failure.retryAfterSeconds : 0,
  );
  return (
    <>
      {failure.reason === "rate-limited" && <RetryCountdownText secondsLeft={secondsLeft} />}
      <Button variant="outline" size="sm" onClick={onRetry} disabled={secondsLeft > 0}>
        {BLOG_CMS_COPY.retry}
      </Button>
    </>
  );
}

function EditorFrame(props: {
  state: FrameState;
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  renewFrameRef: React.RefObject<HTMLIFrameElement | null>;
  retryRenewal: () => void;
}) {
  const { state, iframeRef, renewFrameRef, retryRenewal } = props;
  const showOverlay = !state.ready && !state.silent;
  const reconnecting = (state.silent && !state.ready) || state.renewal?.trigger === "reauth";
  const notice = state.notice;

  // V11: "Reconnected" is also a visible toast; the status keeps it if the toast is dismissed.
  useEffect(() => {
    if (notice === "reconnected") {
      toast({ title: BLOG_CMS_COPY.reconnected });
    }
  }, [notice]);

  const status = reconnecting
    ? BLOG_CMS_COPY.reconnecting
    : notice === "reconnected"
      ? BLOG_CMS_COPY.reconnected
      : "";

  return (
    <div className="relative h-full" aria-busy={showOverlay}>
      <iframe
        ref={iframeRef}
        src={state.src}
        title={BLOG_CMS_COPY.frameTitle}
        sandbox={BLOG_CMS_IFRAME_SANDBOX}
        inert={showOverlay}
        className="h-full w-full border-0"
      />
      {showOverlay && (
        <div className="absolute inset-0 bg-background">
          <BlogCmsStatePanel state="loading" />
        </div>
      )}
      {state.renewal?.src && (
        <iframe
          ref={renewFrameRef}
          src={state.renewal.src}
          sandbox={BLOG_CMS_IFRAME_SANDBOX}
          hidden
          aria-hidden="true"
          tabIndex={-1}
          title={BLOG_CMS_COPY.renewFrameTitle}
          data-testid="blog-cms-renew-frame"
        />
      )}
      {reconnecting && (
        <div
          data-testid="blog-cms-reconnect-bar"
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-0.5 bg-primary motion-safe:animate-pulse"
        />
      )}
      {state.renewFailure && (
        <div
          key={state.renewFailure.seq}
          role="alert"
          data-testid="blog-cms-reconnect-failed"
          className="absolute inset-x-0 top-0 flex flex-wrap items-center gap-3 border-b border-border bg-card px-4 py-2 text-sm"
        >
          <p className="font-medium">{BLOG_CMS_COPY.reconnectFailed}</p>
          <RetryRenewalButton failure={state.renewFailure.failure} onRetry={retryRenewal} />
        </div>
      )}
      <span role="status" className="sr-only">
        {status}
      </span>
    </div>
  );
}

/**
 * `/blog-cms`: EmDash's editor, embedded for SUPER_ADMIN only (D3). The role check here is the
 * gate; `ProtectedRoute` admits SUPPORT and up.
 */
export default function BlogCms() {
  const { user, logout } = useAuth();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const { state, iframeRef, renewFrameRef, retry, retryRenewal } = useBlogCmsFrame({
    enabled: isSuperAdmin,
  });
  const regionRef = useRef<HTMLElement | null>(null);

  const heading = (
    <h1 id="blog-cms-heading" className="sr-only">
      {BLOG_CMS_COPY.heading}
    </h1>
  );

  const handleRetry = () => {
    retry();
    // The focused Retry button unmounts with its panel; the region persists (RV2.9).
    regionRef.current?.focus();
  };

  const handleOpenInNewTab = async () => {
    const result = await openBlogCmsInNewTab();
    if (result.ok) return;
    const { failure } = result;
    const title =
      failure.reason === "blocked"
        ? BLOG_CMS_COPY.popupBlocked
        : failure.reason === "rate-limited"
          ? rateLimitedToast(failure.retryAfterSeconds)
          : BLOG_CMS_COPY.failed;
    toast({ title, variant: "destructive" });
  };

  let body: React.ReactNode;
  if (!isSuperAdmin) {
    body = <BlogCmsStatePanel state="unauthorised" role={user?.role ?? "unknown"} />;
  } else if (state.kind === "ticket") {
    body = <BlogCmsStatePanel state="loading" />;
  } else if (state.kind === "frame") {
    body = (
      <EditorFrame
        state={state}
        iframeRef={iframeRef}
        renewFrameRef={renewFrameRef}
        retryRenewal={retryRenewal}
      />
    );
  } else if (state.kind === "refused") {
    body = (
      <BlogCmsStatePanel
        key={state.seq}
        state="refused"
        failure={state.failure}
        onRetry={handleRetry}
        onSignInAgain={() => void logout()}
      />
    );
  } else {
    body = (
      <BlogCmsStatePanel
        state="unreachable"
        onRetry={handleRetry}
        onOpenInNewTab={() => void handleOpenInNewTab()}
      />
    );
  }

  return (
    <section
      ref={regionRef}
      tabIndex={-1}
      aria-labelledby="blog-cms-heading"
      data-testid="blog-cms-region"
      className={`${REGION_CLASS} outline-none`}
    >
      {heading}
      {isSuperAdmin && (
        <HeaderActions>
          <BlogCmsHeaderActions onOpenInNewTab={() => void handleOpenInNewTab()} />
        </HeaderActions>
      )}
      {body}
    </section>
  );
}
